import React, { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { HeaderMenuProfile } from '@lt/components/components/HeaderMenu';
import { useTranslation } from '@lt/localization';
import { getClientType } from 'src/components/AuthNew/utils/getClientType';
import { removeURLParam } from 'src/components/AuthNew/utils';
import { getShowLogOutPopup } from 'src/store/client/selectors';
import { getPageType } from 'src/store/client/helpers/getPageType';
import classnames from 'classnames';
import { getIsMobile } from 'src/store/view/selectors';
import { AuthFormType, PageType } from '../../../AuthNew/types';
import { useSearchParams } from '../../../AuthNew/hooks/useResetPasswordToken';
import { AuthLoader } from './AuthLoader';
import styles from './headerAuth.module.css';
import { getIconComponent } from './utils';

const AuthNew = lazy(
  () =>
    import(/* webpackChunkName: "AuthNew" */ 'src/components/AuthNew/AuthNew'),
);

const authLayoutCSS = {
  [AuthFormType.Login]: {
    loginFormCSS: css`
      background: #f5f3f2;
    `,
  },
  [AuthFormType.Registration]: {
    wrapperCSS: css`
      width: 600px;
    `,
  },
  [AuthFormType.CreatePassword]: {
    wrapperCSS: css`
      width: 360px;
    `,
  },
};

const HeaderAuth = ({
  className = '',
  isOpen,
  clientName,
  clientAvatar,
  clientUnconfirmedPhone,
  isConfirmedClientEmail,
  onShowAuthPopup,
  onHideAuthPopup,
  onHideLogOutPopup,
  onLoad,
  logIn,
  signUp,
  bonusScore,
  isWhiteLabel,
  isCompact,
  logOut,
  setConfirmedPhone,
  isHomepage,
  mindboxAuth,
  mindboxRegistration,
  showAuthFromWishlist,
  isLoggedIn,
}) => {
  const isMobile = useSelector(getIsMobile);
  const isShownLogOutPopup = useSelector(getShowLogOutPopup);

  const [authLoaded, setAuthLoaded] = useState(false);

  const { show_login_form } = useSearchParams('show_login_form');
  const { confirmation_token } = useSearchParams('confirmation_token');
  const { reset_password_token } = useSearchParams('reset_password_token');

  useEffect(() => {
    if (
      show_login_form ||
      reset_password_token ||
      confirmation_token ||
      showAuthFromWishlist
    ) {
      setAuthLoaded(true);
      onShowAuthPopup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show_login_form,
    reset_password_token,
    confirmation_token,
    showAuthFromWishlist,
  ]);

  const onGetClientType = getClientType(
    clientName,
    isConfirmedClientEmail,
    clientUnconfirmedPhone,
  );

  useEffect(() => {
    if (clientName) {
      const logoutLink = document.querySelector('a[href="/logout"]');
      logoutLink?.addEventListener('click', async (e: Event) => {
        e.preventDefault();
        setAuthLoaded(true);

        logOut();

        const isFBUserExist = window?.FB?.getAccessToken();

        if (isFBUserExist) {
          window.FB.logout();
        }
      });
    }

    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientName, onLoad]);

  const IconComponent = getIconComponent({
    isLoggedIn,
    isWhiteLabel,
    isCompact,
    clientAvatar,
    bonusScore,
  });

  const onSignIn = useCallback(
    (client) => {
      if (!clientName)
        setTimeout(() => {
          logIn(client);
          mindboxAuth();
        }, 2000);
      else
        setTimeout(() => {
          onHideAuthPopup();
        }, 2000);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientName, logIn, mindboxAuth],
  );

  const onSignUp = useCallback(
    (client) => {
      setTimeout(() => {
        signUp(client);
        mindboxRegistration();
      }, 2000);
    },
    [signUp, mindboxRegistration],
  );

  const onLogOut = (shouldShowAuthPopup?: boolean) => {
    logOut();
    if (shouldShowAuthPopup) {
      setAuthLoaded(true);
      onShowAuthPopup();
    }
  };

  const handleSetConfirmedPhone = useCallback(
    (phone) => {
      setTimeout(() => {
        setConfirmedPhone(phone);
      }, 2000);
    },
    [setConfirmedPhone],
  );

  const { t } = useTranslation();

  const afterLogOut = () => {
    // таймаут нужен для того, чтобы при плавном закрытии попапа
    // с логаутом не мелькнул попап с авторизацией
    if (isShownLogOutPopup)
      setTimeout(() => {
        onHideLogOutPopup();
        if (getPageType() === PageType.Client) window.location.reload();
      }, 400);
  };

  const onClose = () => {
    onHideAuthPopup();
    afterLogOut();
  };

  const onCloseModal = () => {
    onClose();
    removeURLParam('redirect_to');
    removeURLParam('show_login_form');
    removeURLParam('reset_password_token');
    removeURLParam('confirmation_token');
    removeURLParam('session_expired');
    removeURLParam('email');
  };

  const name = !isLoggedIn ? t('headerMenu.login') : '';

  return (
    <HeaderMenuProfile
      className={classnames(className, {
        [styles.headeMenuProfile]: !!clientName,
      })}
      slug={isWhiteLabel ? 'wlProfile' : 'profile'}
      name={name}
      showPopup={isOpen}
      isCompact={isCompact}
      isLoggedIn={isLoggedIn}
      isHomepage={isHomepage}
      onOpenModal={() => {
        setAuthLoaded(true);
        onShowAuthPopup();
      }}
      isWhitelabel={isWhiteLabel}
      onCloseModal={onCloseModal}
      IconComponent={IconComponent}
      shouldShowCloseIcon={false}
      PopupAuthComponent={
        authLoaded ? (
          <Suspense fallback={<AuthLoader />}>
            <AuthNew
              isMobile={isMobile}
              isWhiteLabel={isWhiteLabel}
              authLayoutCSS={authLayoutCSS}
              onSignIn={onSignIn}
              onSignUp={onSignUp}
              onLogOut={onLogOut}
              setConfirmedPhone={handleSetConfirmedPhone}
              onClose={onClose}
              clientType={onGetClientType}
              initialFormType={
                isShownLogOutPopup ? AuthFormType.LogOut : AuthFormType.Login
              }
              pageType={getPageType()}
              showCloseButton
            />
          </Suspense>
        ) : (
          <div />
        )
      }
    />
  );
};

export default HeaderAuth;
